import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { useI18n, LangToggle } from "../i18n";
import type { DeviceTarget, PairingCode } from "../types/api";

/**
 * Settings for local-agent accounts: language plus device pairing. Pairing
 * lives here (not on the chat screen) — it's a one-time setup action, while
 * the main screen is for picking a device and chatting.
 */
export function LocalSettingsModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { client, account } = useAuth();
  const { t } = useI18n();
  const [devices, setDevices] = useState<DeviceTarget[]>([]);
  const [pairing, setPairing] = useState<PairingCode | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadDevices = useCallback(async () => {
    if (!client || !account) return;
    try {
      const { localDevices } = await client.listAgents(account.id);
      setDevices(localDevices);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common.failed"));
    }
  }, [client, account, t]);

  // Refresh while open so a device pairing/connecting shows up without a reload.
  useEffect(() => {
    if (!open) return;
    void loadDevices();
    const timer = setInterval(() => void loadDevices(), 5000);
    return () => clearInterval(timer);
  }, [open, loadDevices]);

  // A new pairing code invalidates the displayed one; clear it on close.
  useEffect(() => {
    if (!open) setPairing(null);
  }, [open]);

  async function pair() {
    if (!client || !account) return;
    try {
      setError(null);
      setPairing(await client.createPairingCode(account.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common.failed"));
    }
  }

  // Revoking kicks the router off the relay and frees the slot; the desktop
  // can be paired again with a fresh code.
  async function remove(deviceId: string) {
    if (!client || !account) return;
    try {
      setError(null);
      await client.revokeDevice(account.id, deviceId);
      await loadDevices();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common.failed"));
    }
  }

  if (!open) return null;

  return (
    <div className="modal-root" role="dialog" aria-modal="true" aria-label={t("settings.title")}>
      <div className="modal-scrim" onClick={onClose} />
      <div className="modal-card">
        <div className="modal-head">
          <span className="modal-title">{t("settings.title")}</span>
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t("common.close")} title={t("common.close")}>
            ×
          </button>
        </div>
        <div className="modal-body">
          <div className="settings-lang-row">
            <span className="label settings-lang-label">{t("settings.language")}</span>
            <LangToggle />
          </div>

          <div className="local-devices-head">
            <span className="label">{t("local.devicesTitle")}</span>
            <div className="local-devices-actions">
              <button type="button" className="btn-secondary" onClick={() => void loadDevices()}>
                {t("local.refresh")}
              </button>
              <button type="button" className="btn-secondary" onClick={() => void pair()}>
                {t("local.pair")}
              </button>
            </div>
          </div>

          {pairing ? (
            <div className="pairing-code-box">
              <p className="pairing-code">
                {t("local.pairCode")}: <strong>{pairing.code}</strong>
              </p>
              <p className="muted">{t("local.pairHint")}</p>
            </div>
          ) : null}

          {devices.filter((d) => d.status !== "revoked").length === 0 ? (
            <p className="muted">{t("local.noDevices")}</p>
          ) : (
            <ul className="device-list">
              {devices
                .filter((d) => d.status !== "revoked")
                .map((d) => (
                  <li key={d.id} className="device-row">
                    <span className={`status-dot ${d.online ? "online" : "offline"}`} />
                    <span className="device-name">{d.name}</span>
                    <span className="muted device-platform">{d.platform}</span>
                    <span className="muted">{d.online ? t("local.online") : t("local.offline")}</span>
                    <button
                      type="button"
                      className="icon-btn device-remove"
                      title={t("local.removeDevice")}
                      aria-label={t("local.removeDevice")}
                      onClick={() => void remove(d.id)}
                    >
                      {/* monochrome ✕, matching the app's line icons (emoji render colored on Apple) */}✕
                    </button>
                  </li>
                ))}
            </ul>
          )}

          {error ? <div className="error-banner">{error}</div> : null}
        </div>
      </div>
    </div>
  );
}
